import { useEffect, useRef, useCallback, useState } from "react"

const wait = (wait: number) => new Promise((res, rej) => setTimeout(res, wait))

type ImagesResponse = {
  images: string[]
  status: "complete" | "processing" | "failed" | "canceled"
}

function isImages(value: {}): value is ImagesResponse {
  if ("images" in value) return true
  return false
}

function isSavedImage(value: any) {
  if ("url" in value) return true
  return false
}

const useWebSocket = (wsUrl, autoConnect = true, onClose?: (statusCode: number) => void) => {
  const socketRef = useRef<WebSocket | null>(null)
  const [retryCount, setRetryCount] = useState(0)
  const [response, setResponse] = useState<string[]>([])
  const [inProgress, setInProgress] = useState(false)
  const [statusCode, setStatusCode] = useState(null)
  const [queuedMessage, setQueuedMessage] = useState(null)
  const [savedImage, setSavedImage] = useState<string | null>(null)
  const maxRetries = 5

  const connect = () => {
    socketRef.current = new WebSocket(wsUrl)

    socketRef.current.onmessage = (message: MessageEvent<string>) => {
      const value = JSON.parse(message.data)
      if (isImages(value) && value.status !== "processing") {
        setResponse(value.images)
        setInProgress(false)
      } else if (isSavedImage(value)) {
        setSavedImage(value.url)
      }
    }

    socketRef.current.onerror = err => {
      console.error("Websocket connection err:", err)
      reconnect()
    }

    socketRef.current.onclose = ev => {
      socketRef.current = null
      setStatusCode(ev.code)
      setInProgress(false)
      if (onClose) {
        onClose(ev.code)
      }
    }

    socketRef.current.onopen = ev => {
      setQueuedMessage((prev) => {
          socketRef.current.send(prev)
          return null})
    }

  }

  const reconnect = async () => {
    if (retryCount >= maxRetries) {
      console.error("Max retries reached. Could not reconnect to WebSocket.")
      setInProgress(false)
      return
    }

    console.log(`Reconnecting.. Attempt: ${retryCount + 1}`)
    await wait(1000)

    setRetryCount(prev => prev + 1)
  }

  useEffect(() => {
    if (autoConnect) connect()
  }, [retryCount])

  useEffect(() => {
    if (autoConnect) connect()

    return () => {
      if (!socketRef.current) return
      socketRef.current.close()
    }
  }, [wsUrl])

  useEffect(() => {
    function focusHandler() {
      if (!socketRef.current && autoConnect) connect()
    }

    window.addEventListener("focus", focusHandler)
    return () => {
      window.removeEventListener("focus", focusHandler)
    }
  }, [])

  const disconnect = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.close()
      console.log("WebSocket manually disconnected")
    }
  }, [])

  const sendMessage = useCallback(message => {
    if (socketRef.current) {
      setResponse([])
      setInProgress(true)
      if (socketRef.current.readyState === WebSocket.OPEN) {
        socketRef.current.send(message)
      } else if (socketRef.current.readyState !== WebSocket.OPEN) {
        setQueuedMessage(message)
      }
    }
  }, [])

  return { disconnect, sendMessage, response, inProgress, statusCode, connect, savedImage }
}

export default useWebSocket
